import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { api, getToken, setToken } from './api';

interface AuthState {
  email: string | null;
  signOut: () => void;
  /** Set when a request 401s mid-session, so an editor can hold its draft and retry. */
  expired: boolean;
  clearExpired: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

function Centred({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 3 }}>{children}</Box>
  );
}

function SignIn({ onToken }: { onToken: (token: string, email: string) => void }) {
  const [error, setError] = useState<string | null>(null);

  return (
    <Centred>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }} elevation={0} variant="outlined">
        <Stack spacing={2.5} alignItems="flex-start">
          <Box>
            <Typography variant="h5" fontWeight={600}>
              DWLF Tickets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The delivery board. Sign in with your dwlf.co.uk account.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <GoogleLogin
            onSuccess={async (credential) => {
              setError(null);
              if (!credential.credential) {
                setError('Google returned no credential. Try again.');
                return;
              }
              try {
                const session = await api.exchangeGoogle(credential.credential);
                onToken(session.token, session.email);
              } catch (cause) {
                // The API's own message is the useful one here: it distinguishes a
                // non-dwlf.co.uk account from an unverified email from an address that
                // is simply not on the allowlist.
                setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
              }
            }}
            onError={() => setError('Google sign-in was cancelled or blocked.')}
          />
        </Stack>
      </Paper>
    </Centred>
  );
}

interface Bootstrap {
  clientId: string | null;
  bootError: string | null;
  restoredEmail: string | null;
  ready: boolean;
}

/**
 * Fetches the public Google client ID, then restores a stored session if there is one.
 * Split out of the provider so the provider stays about session STATE rather than about
 * the order these two independent things happen in.
 */
function useBootstrap(): Bootstrap {
  const [state, setState] = useState<Bootstrap>({
    clientId: null,
    bootError: null,
    restoredEmail: null,
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;
    const settle = (patch: Partial<Bootstrap>) =>
      !cancelled && setState((current) => ({ ...current, ...patch }));

    (async () => {
      try {
        settle({ clientId: (await api.authConfig()).googleClientId });
      } catch {
        // Fail loud rather than rendering a sign-in button that cannot work: the most
        // likely cause is that /dwlf-tickets/google-client-id has not been set.
        settle({ bootError: 'The board could not load its sign-in configuration.' });
      }

      if (getToken()) {
        try {
          settle({ restoredEmail: (await api.whoami()).principal.email });
        } catch {
          setToken(null); // A stored token that no longer works is simply not a session.
        }
      }
      settle({ ready: true });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { clientId, bootError, restoredEmail, ready } = useBootstrap();
  const [email, setEmail] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  // Adopted as state rather than read directly, so signing out actually signs you out —
  // deriving `email` from the bootstrap result would let it reappear on the next render.
  useEffect(() => {
    if (restoredEmail) setEmail(restoredEmail);
  }, [restoredEmail]);

  const signOut = useCallback(() => {
    setToken(null);
    setEmail(null);
    setExpired(false);
  }, []);

  useEffect(() => {
    const onExpiry = () => {
      setEmail(null);
      setExpired(true);
    };
    window.addEventListener('dwlf-session-expired', onExpiry);
    return () => window.removeEventListener('dwlf-session-expired', onExpiry);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ email, signOut, expired, clearExpired: () => setExpired(false) }),
    [email, signOut, expired],
  );

  if (!ready) {
    return (
      <Centred>
        <CircularProgress />
      </Centred>
    );
  }

  if (bootError || !clientId) {
    return (
      <Centred>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          {bootError ?? 'No Google client is configured for this board.'}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Set <code>/dwlf-tickets/google-client-id</code> in SSM.
          </Typography>
        </Alert>
      </Centred>
    );
  }

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <AuthContext.Provider value={value}>
        {email ? (
          children
        ) : (
          <SignIn
            onToken={(token, signedInAs) => {
              setToken(token);
              setEmail(signedInAs);
              setExpired(false);
            }}
          />
        )}
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
}
